/**
 * NotificationPreferencesPage — Settings shell for notification preferences
 *
 * Gated behind ENABLE_NOTIFICATION_PREFS. When OFF, /settings and
 * /settings/notifications both redirect to /preferences.
 *
 * Section A: Current Live Controls (read-only, links to /preferences)
 * Section B: Future Saved Preferences (localStorage, not yet enforced)
 *
 * This page does NOT change any live delivery behavior.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotificationPrefs,
  saveNotificationPrefs,
  DAYS_OF_WEEK,
  type NotificationPrefs,
  type DeliveryChannel,
  type DayOfWeek,
} from '../config/notificationPreferences';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Bell,
  Mail,
  Clock,
  ExternalLink,
  Info,
} from 'lucide-react';

const NotificationPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const enabled = useFeatureFlag('enable_notification_prefs');

  // Flag OFF → safe redirect
  if (!enabled) {
    return <Navigate to="/preferences" replace />;
  }

  const userId = (user as any)?.user_id || '';

  return <NotificationPrefsContent userId={userId} navigate={navigate} />;
};

/**
 * Inner content — separated to avoid hooks-after-early-return lint issues.
 */
const NotificationPrefsContent: React.FC<{
  userId: string;
  navigate: ReturnType<typeof useNavigate>;
}> = ({ userId, navigate }) => {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);

  useEffect(() => {
    if (userId) setPrefs(getNotificationPrefs(userId));
  }, [userId]);

  const save = (updated: NotificationPrefs) => {
    setPrefs(updated);
    saveNotificationPrefs(userId, updated);
    toast.success('Preferences saved');
  };

  if (!prefs) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="notification-prefs-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Notification Preferences</h1>
            <p className="text-sm text-slate-500">Manage how and when you receive notifications</p>
          </div>
        </div>

        {/* ─── Section A: Current Live Controls (read-only) ─── */}
        <div className="mb-8 p-5 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="section-live-controls">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-emerald-600" />
            <h2 className="text-sm font-bold text-slate-800">Current Email Delivery</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Email delivery for literature queues is currently managed <strong>per queue</strong> in your queue settings.
            Each queue has its own email notification toggle and delivery schedule.
          </p>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => navigate('/preferences')}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-lg hover:bg-sky-100 transition-colors"
              data-testid="manage-queues-link"
            >
              Manage your queues <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => navigate('/inbox')}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="view-inbox-link"
            >
              <Bell className="w-3.5 h-3.5" /> View Inbox
            </button>
          </div>
        </div>

        {/* ─── Section B: Future Saved Preferences ─── */}
        <div className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="section-future-prefs">
          {/* Not-yet-enforced banner */}
          <div className="flex items-start gap-2 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="future-banner">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              <strong>Saved for future rollout.</strong> These preferences are stored locally and will be
              enforced in a future update. They do not affect current email delivery.
            </p>
          </div>

          {/* Quiet Hours */}
          <div className="mb-6" data-testid="quiet-hours-section">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-800">Quiet Hours</h3>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.quietHours.enabled}
                  onChange={(e) => save({ ...prefs, quietHours: { ...prefs.quietHours, enabled: e.target.checked } })}
                  className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  data-testid="quiet-hours-toggle"
                />
                <span className="text-sm text-slate-700">Enable quiet hours</span>
              </label>
            </div>

            {prefs.quietHours.enabled && (
              <div className="ml-6 space-y-3">
                {/* Time range */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500">From</span>
                  <select value={prefs.quietHours.startHour} onChange={e => save({ ...prefs, quietHours: { ...prefs.quietHours, startHour: parseInt(e.target.value) } })}
                    className="px-2 py-1 text-xs border rounded" data-testid="quiet-start-hour">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <select value={prefs.quietHours.startAmpm} onChange={e => save({ ...prefs, quietHours: { ...prefs.quietHours, startAmpm: e.target.value as 'AM' | 'PM' } })}
                    className="px-2 py-1 text-xs border rounded" data-testid="quiet-start-ampm">
                    <option value="AM">AM</option><option value="PM">PM</option>
                  </select>
                  <span className="text-xs text-slate-500">to</span>
                  <select value={prefs.quietHours.endHour} onChange={e => save({ ...prefs, quietHours: { ...prefs.quietHours, endHour: parseInt(e.target.value) } })}
                    className="px-2 py-1 text-xs border rounded" data-testid="quiet-end-hour">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <select value={prefs.quietHours.endAmpm} onChange={e => save({ ...prefs, quietHours: { ...prefs.quietHours, endAmpm: e.target.value as 'AM' | 'PM' } })}
                    className="px-2 py-1 text-xs border rounded" data-testid="quiet-end-ampm">
                    <option value="AM">AM</option><option value="PM">PM</option>
                  </select>
                </div>

                {/* Days of week */}
                <div className="flex flex-wrap gap-1">
                  {DAYS_OF_WEEK.map(day => {
                    const active = prefs.quietHours.days.includes(day);
                    return (
                      <button key={day} onClick={() => {
                        const days = active
                          ? prefs.quietHours.days.filter(d => d !== day)
                          : [...prefs.quietHours.days, day];
                        save({ ...prefs, quietHours: { ...prefs.quietHours, days: days as DayOfWeek[] } });
                      }}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                          active ? 'bg-sky-100 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-400'
                        }`}
                        data-testid={`quiet-day-${day}`}
                      >{day}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Preferred Delivery Channel */}
          <div data-testid="delivery-section">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-800">Preferred Delivery</h3>
            </div>
            <div className="space-y-2 ml-6">
              {([
                { value: 'email' as DeliveryChannel, label: 'Email only', desc: 'Receive notifications via email' },
                { value: 'email_and_inapp' as DeliveryChannel, label: 'Email + In-app', desc: 'Email and in-app notifications (when available)' },
              ]).map(opt => (
                <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="delivery"
                    value={opt.value}
                    checked={prefs.delivery === opt.value}
                    onChange={() => save({ ...prefs, delivery: opt.value })}
                    className="mt-0.5 w-4 h-4 text-sky-600 focus:ring-sky-500"
                    data-testid={`delivery-${opt.value}`}
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesPage;
